// @file composable for native bridge listeners
// TODO: [to-be-migrated][post_action]
// TODO: [to-be-migrated][surface]

import appCan from '@@/bits/app_can'
import device from '@@/bits/device'
import { DOMContentLoadedPromise } from '@@/bits/dom'
import window from '@@/bits/global'
import { buildUrlFromPath, currentPath, navigateTo } from '@@/bits/location'
import { getVuexStore } from '@@/bits/pinia'
import { setSafeAreaInsets } from '@@/bits/safe_area_insets'
import type { EdgeInsets } from '@@/bits/window'
import {
  firstTouch as nativeBridgeFirstTouchMessage,
  openLink as nativeBridgeOpenLinkMessage,
  pageReady as nativeBridgePageReadyMessage,
} from '@@/native_bridge/actions'
import { listen } from '@@/native_bridge/listen'
import postMessage from '@@/native_bridge/post_message'
import type { LinkType, ListenedMessage } from '@@/native_bridge/types'
import { useExpandedPostStore } from '@@/pinia/expanded_post'
import type { LinkTarget, NativeAppOpenLink } from '@@/pinia/native_app'
import { useNativeAppStore } from '@@/pinia/native_app'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { RootState } from '@@/vuexstore/surface/types'
import { storeToRefs } from 'pinia'
import { watch } from 'vue'

const BRIDGED_LINK_TYPE_ATTRIBUTE = 'data-bridged-link'

interface Props {
  for: 'slideshow' | 'surface'
}

export const useNativeBridgeListeners = (props: Props): void => {
  const surfaceVuexStore = getVuexStore<RootState>()
  const nativeAppStore = useNativeAppStore()
  const surfaceStore = useSurfaceStore()

  const { isSlideshow } = storeToRefs(surfaceStore)
  const { expandedPost } = storeToRefs(useExpandedPostStore())
  const { linkToOpen } = storeToRefs(nativeAppStore)
  const { postEntitiesById } = storeToRefs(useSurfacePostsStore())
  const { postSurfaceState, hideAllPanelsAndMenus, openLink, clearOpenLink } = nativeAppStore
  const { refresh } = surfaceStore

  /* ----------------------------- */
  /* GLOBAL LISTENERS              */
  /* ----------------------------- */
  /**
   * Posts a message when the DOM is ready.
   * If the device is not iOS or Android, or if the current path includes 'beethoven/attach' or 'mobile-app/attach',
   * or if the code is running inside an iframe, the function will return early.
   * If the code is running inside a slideshow, it will also return early since we don't want to redirect users to a wall not found page, since it is not a wall.
   * Otherwise, it will post a message using the native bridge and navigate to a specific URL.
   */
  const postMessageOnDomReady = (): void => {
    if (!(device.ios || device.android)) return
    if (currentPath().includes('beethoven/attach') || currentPath().includes('mobile-app/attach')) return

    const isDisplayedInIFrame = window.self !== window.top
    if (isDisplayedInIFrame) return

    if (isSlideshow.value) return

    postMessage(nativeBridgePageReadyMessage(), () => navigateTo(buildUrlFromPath('/ios-app/page-ready')))
    void postSurfaceState()
  }

  const installGlobalListeners = (): void => {
    document.body.addEventListener('click', onDataBridgedLinkClick)
    listen('refresh_padlet', () => {
      // manually refresh the padlet when this messaged is received from native app
      refresh({ isAutoRefresh: false })
    })
    listen('cancel_show', hideAllPanelsAndMenus)
    listen('post_surface_state', postSurfaceState)
    void DOMContentLoadedPromise.then(postMessageOnDomReady)
  }

  /* ----------------------------- */
  /* SURFACE SPECIFIC LISTENERS    */
  /* ----------------------------- */
  const installExpandedPostListeners = (): void => {
    listen('hide_expanded_post', (): void => {
      void surfaceVuexStore?.dispatch('postAction/unexpandPost')
      void postSurfaceState()
    })
    // if the post has just been created by the mobile app's native post editor, postCid inside the webview will be c_new<number> instead of c<post_id>
    // => to be safe, find post by id first in set_expanded_post_id and show_post_action_menu
    listen('set_expanded_post_id', (message: ListenedMessage & { payload: { post_id: string } }): void => {
      const postCid = postEntitiesById.value[message.payload.post_id]?.cid
      if (postCid == null) {
        return
      }
      void surfaceVuexStore?.dispatch('postAction/expandPost', { postCid })
      void postSurfaceState()
    })
    listen('show_post_action_menu', (message: ListenedMessage & { payload: { post_id: string } }): void => {
      const postCid = postEntitiesById.value[message.payload.post_id]?.cid
      if (postCid == null) {
        return
      }
      void surfaceVuexStore?.dispatch('postAction/showPostActionMenu', { postCid })
    })
    listen('hide_post_action_menu', (): void => {
      void surfaceVuexStore?.dispatch('postAction/hidePostActionMenu')
    })
  }

  /* ----------------------------- */
  /* NATIVE APP SPECIFIC LISTENERS */
  /* ----------------------------- */
  /**
   * Handles the click event on anchor tags with the attribute 'data-bridged-link'.
   * @param e - The click event.
   */
  const onDataBridgedLinkClick = (e: Event): void => {
    const clickTarget = e.target as HTMLElement
    // only process anchor tags
    const target = clickTarget.closest('a')
    if (target == null) return
    // only process anchor tags with our special class
    if (target.hasAttribute(BRIDGED_LINK_TYPE_ATTRIBUTE)) {
      const anchorHref = target.getAttribute('href')
      const anchorTarget = target.getAttribute('target') as LinkTarget | null
      const linkType = target.getAttribute(BRIDGED_LINK_TYPE_ATTRIBUTE) as LinkType | null
      e.preventDefault()
      const openLinkPayload: NativeAppOpenLink = {
        linkType,
        target: anchorTarget,
        url: anchorHref,
      }

      switch (linkType) {
        case 'padlet':
          openLinkPayload.contentCategory = 'page'
          openLinkPayload.contentSubcategory = 'padlet'
          break
        case 'slideshow':
          openLinkPayload.contentCategory = 'page'
          openLinkPayload.contentSubcategory = 'slideshow'
          break
        case 'profile':
          openLinkPayload.contentCategory = 'page'
          openLinkPayload.contentSubcategory = 'profile'
          openLinkPayload.username = target.getAttribute('data-bridged-link-username')
          break
      }
      openLink(openLinkPayload)
    }
  }

  const installSafeAreaListener = (): void => {
    listen('set_safe_area_insets', (message: { message_type: string; safeAreaInsets: EdgeInsets }) => {
      setSafeAreaInsets(message.safeAreaInsets)
    })
  }

  /* ----------------------------- */
  /* MOBILE-SPECIFIC LISTENERS     */
  /* ----------------------------- */
  // Collapses header on first touch
  // Only collapse if the touch is in vertical direction
  // After that, no need to listen for touch events anymore
  // since the header is already collapsed
  const installFirstTouchListenerForNativeHeader = (): void => {
    if (!appCan('useNativeHeader')) return

    let initialTouchX = 0
    let initialTouchY = 0
    const TOUCH_THRESHOLD = 10 // pixels

    const handleTouchStart = (e: TouchEvent): void => {
      initialTouchX = e.touches[0].clientX
      initialTouchY = e.touches[0].clientY
    }

    const handleTouchEnd = (e: TouchEvent): void => {
      const finalTouchX = e.changedTouches[0].clientX
      const finalTouchY = e.changedTouches[0].clientY
      const deltaX = Math.abs(finalTouchX - initialTouchX)
      const deltaY = Math.abs(finalTouchY - initialTouchY)
      const isVerticalTouch = deltaY / deltaX > 0 && deltaY > TOUCH_THRESHOLD
      if (isVerticalTouch) {
        postMessage(nativeBridgeFirstTouchMessage({ isVerticalTouch }))
        document.body.removeEventListener('touchstart', handleTouchStart)
        document.body.removeEventListener('touchend', handleTouchEnd)
      }
    }

    document.body.addEventListener('touchstart', handleTouchStart)
    document.body.addEventListener('touchend', handleTouchEnd)
  }
  /* ---------------------- */
  /* WATCHERS               */
  /* ---------------------- */
  watch(expandedPost, async () => {
    await postSurfaceState()
  })

  watch(linkToOpen, (newValue, oldValue) => {
    if (newValue != null && oldValue == null) {
      const { linkType, url, contentCategory, contentSubcategory, username } = newValue
      postMessage(nativeBridgeOpenLinkMessage({ url, linkType, contentCategory, contentSubcategory, username }))
      clearOpenLink()
    }
  })
  /* ---------------------- */
  /* ACTIONS                */
  /* ---------------------- */
  const setupSurfaceListeners = (): void => {
    installGlobalListeners()
    installFirstTouchListenerForNativeHeader()
    installExpandedPostListeners()
  }

  const setupSlideshowListeners = (): void => {
    installGlobalListeners()
    installSafeAreaListener()
  }

  const setupListeners = (): void => {
    if (props.for === 'surface') {
      setupSurfaceListeners()
      return
    }

    if (props.for === 'slideshow') {
      setupSlideshowListeners()
    }
  }

  setupListeners()
}
